import * as React from 'react';

import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {MeetingFragment as Meeting, TimespanInput} from 'nutshell-graphql-types';

import {useUpdateMeeting} from '../graphql/hooks';

import {AdditionalAttendees} from './additional-attendees';
import {BufferTime} from './buffer-time';
import {MinimumNotice} from './minimum-notice';
import {ReminderTiming} from './reminder-timing';
import {StartTimes} from './start-times';

import styles from './advanced-options.module.css';

type Props = {
    meeting: Meeting;
    meetingPreview: React.ReactNode;
    postSave: () => void;
};

export function AdvancedOptions(props: Props) {
    const [isSavingMeetingOptions, setIsSavingMeetingOptions] = React.useState<boolean>(false);
    const {toastState} = useManageSaveState(isSavingMeetingOptions, 1000);
    // Store which piece of data is saving so we can show a saving spinner by that label
    const [savingToastKey, setSavingToastKey] = React.useState<string | null | undefined>(null);
    const updateMeeting = useUpdateMeeting();

    const handleSave = (input: {
        minNotice?: TimespanInput;
        bufferBeforeMins?: number | null | undefined;
        bufferAfterMins?: number | null | undefined;
        startTimeIntervalMinutes?: number;
        sendConfirmationReminder?: boolean;
        sendUpcomingReminder?: boolean;
        sendSoonReminder?: boolean;
        allowAdditionalAttendees?: boolean;
    }) => {
        setIsSavingMeetingOptions(true);
        updateMeeting({
            id: props.meeting.id,
            minNotice: input.minNotice,
            bufferBeforeMins: input.bufferBeforeMins,
            bufferAfterMins: input.bufferAfterMins,
            startTimeIntervalMinutes: input.startTimeIntervalMinutes,
            sendConfirmationReminder: input.sendConfirmationReminder,
            sendUpcomingReminder: input.sendUpcomingReminder,
            sendSoonReminder: input.sendSoonReminder,
            allowAdditionalAttendees: input.allowAdditionalAttendees,
        }).then(() => {
            setIsSavingMeetingOptions(false);
            props.postSave();
            // Show the saving toast for 1 second
            setTimeout(() => {
                // setting to null so it doesn't show up for other fields too.
                setSavingToastKey(null);
            }, 1000);
        });
    };

    return (
        <PageContentTwoColumn
            withoutOuterPadding={true}
            leftContent={
                <LabelWithContent
                    label='Preview'
                    spacing={8}
                    isFullHeight={true}
                    content={
                        <div className={styles['preview-container']}>{props.meetingPreview}</div>
                    }
                />
            }
            rightContent={
                <StackLegacy spacing={16}>
                    <LabelWithContent
                        label='Reminders'
                        state={savingToastKey === 'reminders' ? toastState : 'initial'}
                        sublabel='Send a reminder email to avoid manual follow-up with invitees:'
                        spacing={8}
                        content={
                            <ReminderTiming
                                onSave={(input) => {
                                    handleSave(input);
                                }}
                                setSavingToastKey={setSavingToastKey}
                                sendConfirmationReminder={props.meeting.sendConfirmationReminder}
                                sendUpcomingReminder={props.meeting.sendUpcomingReminder}
                                sendSoonReminder={props.meeting.sendSoonReminder}
                            />
                        }
                    />
                    <LabelWithContent
                        label='Minimum notice'
                        state={savingToastKey === 'minimum-notice' ? toastState : 'initial'}
                        sublabel='The minimum amount of notice required before someone can book this meeting:'
                        spacing={8}
                        content={
                            <MinimumNotice
                                onSave={(minNotice: TimespanInput) => handleSave({minNotice})}
                                setSavingToastKey={setSavingToastKey}
                                minNotice={props.meeting.minNotice}
                            />
                        }
                    />
                    <LabelWithContent
                        label='Start times'
                        state={savingToastKey === 'start-times' ? toastState : 'initial'}
                        sublabel='Show invitees time slots in the following increments:'
                        spacing={8}
                        content={
                            <StartTimes
                                onSave={(startTimeIntervalMinutes: number) =>
                                    handleSave({startTimeIntervalMinutes})
                                }
                                setSavingToastKey={setSavingToastKey}
                                startTimeIntervalMinutes={props.meeting.startTimeIntervalMinutes}
                            />
                        }
                    />
                    <LabelWithContent
                        label='Buffer time'
                        state={savingToastKey === 'buffer' ? toastState : 'initial'}
                        sublabel='Reserve time before or after this meeting is booked'
                        spacing={8}
                        content={
                            <BufferTime
                                onSave={(
                                    bufferBeforeMins?: number | null,
                                    bufferAfterMins?: number | null
                                ) => handleSave({bufferBeforeMins, bufferAfterMins})}
                                setSavingToastKey={setSavingToastKey}
                                bufferBeforeMins={props.meeting.bufferBeforeMins}
                                bufferAfterMins={props.meeting.bufferAfterMins}
                            />
                        }
                    />
                    <LabelWithContent
                        label='Allow additional attendees'
                        state={savingToastKey === 'additional-attendees' ? toastState : 'initial'}
                        spacing={8}
                        content={
                            <AdditionalAttendees
                                onSave={(input) => {
                                    handleSave(input);
                                }}
                                setSavingToastKey={setSavingToastKey}
                                allowAdditionalAttendees={props.meeting.allowAdditionalAttendees}
                            />
                        }
                    />
                </StackLegacy>
            }
        />
    );
}
